import { useEffect, useRef } from "react";

import { useNavigate, useQueryParamsManager } from "../routing";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectCurrentClassUser,
  selectCurrentPageId,
  selectCurrentSectionId,
  selectIsPresentationActive,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { viewStateActions } from "store/slices/view";
import { pageUrl } from "utils/urls";

interface UseBreakoutInSessionObserverProps {
  isInVirtualClass: boolean;
}

export const useBreakoutInSessionObserver = ({
  isInVirtualClass,
}: UseBreakoutInSessionObserverProps) => {
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isPresentationActive = useAppSelector(selectIsPresentationActive);
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const classSession = useAppSelector(selectActiveClassSession);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentPageId = useAppSelector(selectCurrentPageId);
  const { canParticipateInSection } = useCapabilities();
  const { deleteQueryParams } = useQueryParamsManager();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const breakoutGroupId = currentClassUser?.breakout_group_id;
  const syncedPageId = classSession?.breakout_session_synced_page_id;

  const previousBreakoutSessionEndsAt = useRef<string | null | undefined>(undefined);
  const previousBreakoutGroupId = useRef<string | null | undefined>(undefined);
  const previousIsInVirtualClass = useRef(isInVirtualClass);

  useEffect(() => {
    if (isInVirtualClass && isInBreakout && !!breakoutGroupId) {
      deleteQueryParams([FOLLOWING_QUERY_PARAM]);
      dispatch(viewStateActions.setHasStudentLeftPresentation(false));
    }
  }, [
    isInBreakout,
    isPresentationActive,
    deleteQueryParams,
    dispatch,
    isInVirtualClass,
    breakoutGroupId,
  ]);

  useEffect(() => {
    const breakoutSessionEndsAt = classSession?.breakout_session_ends_at;

    if (
      !isInVirtualClass ||
      !isInBreakout ||
      !canParticipateInSection ||
      !sectionId ||
      !syncedPageId ||
      syncedPageId === currentPageId
    ) {
      previousBreakoutSessionEndsAt.current = breakoutSessionEndsAt;
      previousBreakoutGroupId.current = breakoutGroupId;
      previousIsInVirtualClass.current = isInVirtualClass;
      return;
    }

    const breakoutJustStarted = !previousBreakoutSessionEndsAt.current && !!breakoutSessionEndsAt;
    const assignedToBreakoutGroup =
      !!breakoutGroupId && breakoutGroupId !== previousBreakoutGroupId.current;
    const joinedVirtualClassDuringBreakout = isInVirtualClass && !previousIsInVirtualClass.current;

    if (breakoutJustStarted || assignedToBreakoutGroup || joinedVirtualClassDuringBreakout) {
      navigate(pageUrl(sectionId, syncedPageId));
    }

    previousBreakoutSessionEndsAt.current = breakoutSessionEndsAt;
    previousBreakoutGroupId.current = breakoutGroupId ?? null;
    previousIsInVirtualClass.current = isInVirtualClass;
  }, [
    isInVirtualClass,
    isInBreakout,
    canParticipateInSection,
    sectionId,
    syncedPageId,
    currentPageId,
    classSession?.breakout_session_ends_at,
    breakoutGroupId,
    navigate,
  ]);
};
